/**
 * Hiérarchie des codes d'UO (portage de EditorPreferences Android / Preference.swift iOS).
 * Les niveaux d'un code d'UO sont séparés par un anti-slash (ex : GRP\FILIALE\AGENCE).
 */

/** Séparateur des niveaux dans un code d'UO */
export const OU_CODE_SEPARATOR = '\\';

/**
 * Normalise un code d'UO : espaces supprimés, séparateurs doublés ou finaux retirés.
 * Un séparateur initial éventuel est conservé. Ex : " BTBS\\IDF \ Paris\ " -> "BTBS\IDF\Paris"
 *
 * @returns le code normalisé, vide si absent ou sans contenu
 */
export const normalizeOuCode = (code: string | null | undefined): string => {
  const trimmed = (code ?? '').trim();
  if (!trimmed) return '';

  const normalized = trimmed
    .split(OU_CODE_SEPARATOR)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .join(OU_CODE_SEPARATOR);
  if (!normalized) return '';

  return trimmed.startsWith(OU_CODE_SEPARATOR) ? `${OU_CODE_SEPARATOR}${normalized}` : normalized;
};

const stripLeadingSeparator = (code: string): string =>
  code.startsWith(OU_CODE_SEPARATOR) ? code.substring(OU_CODE_SEPARATOR.length) : code;

/**
 * Clé de comparaison d'un code d'UO (Map, dédoublonnage) : code normalisé, sans séparateur initial, en minuscules.
 * Deux codes ont la même clé si et seulement si isSameOuCode.
 */
export const ouCodeKey = (code: string | null | undefined): string =>
  stripLeadingSeparator(normalizeOuCode(code)).toLowerCase();

/** Comparaison de deux codes d'UO sans tenir compte de la casse ni d'un séparateur initial. Un code vide n'est égal à aucun autre. */
export const isSameOuCode = (lhs: string | null | undefined, rhs: string | null | undefined): boolean => {
  const left = ouCodeKey(lhs);
  return !!left && left === ouCodeKey(rhs);
};

/** Vrai si ancestorCode désigne un ancêtre strict de l'UO code */
export const isOuCodeAncestor = (ancestorCode: string | null | undefined, code: string | null | undefined): boolean => {
  const ancestorKey = ouCodeKey(ancestorCode);
  return !!ancestorKey && ouCodeKey(code).startsWith(`${ancestorKey}${OU_CODE_SEPARATOR}`);
};

/** Profondeur d'un code d'UO (nombre de niveaux) */
export const ouCodeDepth = (code: string | null | undefined): number => {
  const key = ouCodeKey(code);
  return key ? key.split(OU_CODE_SEPARATOR).length : 0;
};
